/**
 * Landing page content, verbatim from the design handoff.
 *
 * Extracted from the component so the copy is reviewable on its own and the
 * unverified claims below are visible in one place rather than scattered
 * through JSX.
 */

/**
 * The cohort size shown to users.
 *
 * 204 is the real number of rows the scan ranks against: `source = 'anchored'`,
 * the current `cohort_version`. It is the ONLY count in this file backed by
 * data, and it must track the cohort — if the cohort is re-scored or extended,
 * this changes with it. `apps/web/src/routes/landing-content.test.ts` asserts
 * it against the seed so the two cannot drift apart silently.
 *
 * It replaced "4,216", which was never true — the copy was reproduced from the
 * handoff as final while the table held 20 synthetic rows. A precise-looking
 * count is read as a database figure, so the invented precision made it worse
 * than a round number: it is the first claim an adversarial reader checks, and
 * it sat next to the one asset that can actually be defended.
 *
 * Say what 204 IS rather than apologising for what it is not. "204 real
 * companies whose outcomes we know" is a stronger claim than a large scrape,
 * and it survives scrutiny.
 */
export const COHORT_SIZE = '204';

/**
 * ⚠️ CLAIMS NOT YET SUPPORTED BY DATA — a product decision, not an oversight.
 *
 * The handoff treats all copy as final, so these are reproduced as designed
 * rather than silently softened. They are collected here so changing them is
 * one edit:
 *
 *   reportsDelivered — the research pipeline is out of scope (BACKLOG.md §7),
 *                      so no reports have been delivered at all.
 *   medianTurnaround — likewise, nothing has been turned around yet.
 *
 * `ideasEvaluated` used to live here and has been replaced by COHORT_SIZE
 * above. These two remain false and are unchanged: they were outside the scope
 * of that fix, and softening them is a copy decision that is yours to make.
 * See BACKLOG.md §6 risk 1.
 */
export const CLAIMS = {
  reportsDelivered: '3,982',
  medianTurnaround: '31h',
} as const;

export const DELIVERABLES = [
  {
    eyebrow: 'DELIVERABLE 01 · PDF',
    title: 'Executive deck',
    body: '10 slides you can forward to a co-founder or an investor. Verdict, scores, competitive map — plus a shareable verdict link.',
    art: 'deck',
    accent: false,
  },
  {
    eyebrow: 'DELIVERABLE 02 · PDF',
    title: 'Detailed research report',
    body: "~18 pages with sources. Market sizing, competitor teardowns, timing evidence, risk register, and the questions we couldn't answer.",
    art: 'report',
    accent: false,
  },
  {
    eyebrow: 'DELIVERABLE 03 · INSTANT',
    title: 'Instant scan',
    body: `Your percentile in 40 seconds — scored against ${COHORT_SIZE} real companies whose outcomes we know, before the deep research even starts.`,
    art: 'scan',
    accent: true,
  },
] as const;

export const HOW_IT_WORKS = [
  {
    n: '1',
    title: 'Describe your idea in a guided chat',
    body: "One question at a time, about six minutes. The AI interviewer asks the follow-ups a good investor would, and drills in when an answer is vague. Skip anything you don't know yet.",
  },
  {
    n: '2',
    title: 'See your scan, then sharpen it',
    body: `The moment you sign in, your idea is scored against ${COHORT_SIZE} real companies whose outcomes we know — and the AI asks four questions aimed at your specific gaps. Answer or pass each one.`,
  },
  {
    n: '3',
    title: 'Deep research lands in 48h',
    body: 'Agents read a few hundred sources — market, competitors, timing, risks — with every claim cited. The executive deck and full report arrive by email.',
  },
] as const;

export const PROOF_STATS = [
  // "REAL COMPANIES SCORED", not "IDEAS EVALUATED": the strength of 204 is
  // entirely in what the rows are. The old label invited the comparison this
  // number loses (a scraped database of thousands) instead of the one it wins.
  { value: COHORT_SIZE, label: 'REAL COMPANIES SCORED' },
  { value: CLAIMS.reportsDelivered, label: 'REPORTS DELIVERED' },
  { value: CLAIMS.medianTurnaround, label: 'MEDIAN TURNAROUND' },
] as const;

export const PULL_QUOTE = {
  text: "It found two competitors I'd never heard of and talked me out of six months building the wrong wedge.",
  attribution:
    'FOUNDER OF THE SAMPLE REPORT BELOW · PUBLISHED WITH PERMISSION',
} as const;

export const DECK_SLIDES = [
  { no: '01', title: 'Verdict & percentile' },
  { no: '02', title: 'The idea, restated' },
  { no: '03', title: 'Problem severity' },
  { no: '04', title: 'Competitive map' },
  { no: '05', title: 'Biggest risk' },
  { no: '06', title: 'What to validate next' },
] as const;

/** The sample report shown under the "Detailed report" tab. */
export const SAMPLE_REPORT = {
  meta: 'IDEA REPORT · 12 JULY 2026 · 214 SOURCES READ',
  title:
    'Meal planning built around rotating shift schedules, for nurses and warehouse crews.',
  score: '6.8',
  // 88th, not 12th: the label switched from "TOP n%" (low = good) to a true
  // percentile (high = good), so the exemplar inverts to keep meaning the same
  // placement — and to stay consistent with the 6.8 score beside it.
  // No count here. This is a per-domain claim ("consumer-health"), so the full
  // cohort size would be the wrong denominator even now that it is truthful —
  // the old copy put the whole-cohort figure against a subset, which is how a
  // number can be accurate and still misleading.
  percentile: `88TH PERCENTILE OF THE CONSUMER-HEALTH COMPANIES WE'VE SCORED`,
  verdict:
    'A real, underserved scheduling problem with a weak substitute set — but the wedge is retention, not acquisition, and nothing in the plan yet addresses week four.',
  scores: [
    { label: 'Problem severity', value: 8.4 },
    { label: 'Market timing', value: 7.1 },
    { label: 'Competition intensity', value: 3.8 },
    { label: 'Defensibility', value: 5.2 },
    { label: 'Business model strength', value: 6.4 },
  ],
  risk: 'Meal-planning apps lose 60–70% of users inside 30 days. Shift workers churn hardest in the weeks they most need help — when the schedule breaks.',
  signal:
    "Three large hospital systems now subsidise employee wellbeing tools directly. That's a paying buyer who isn't the user — and nobody is selling them this.",
  competitors: [
    {
      name: 'Mealime',
      what: 'Consumer meal plans, fixed weekly cadence',
      gap: 'No schedule awareness at all',
    },
    {
      name: 'Eat This Much',
      what: 'Macro-driven auto meal generation',
      gap: "Optimises nutrition, ignores when you're awake",
    },
    {
      name: 'HelloFresh',
      what: 'Meal kits on a weekly delivery slot',
      gap: 'Delivery windows assume office hours',
    },
    {
      name: 'Shyft / Deputy',
      what: 'Shift scheduling for hourly teams',
      gap: 'Owns the roster data, does nothing with wellbeing',
    },
  ],
  validate: [
    'Run a four-week manual concierge with 20 night-shift nurses. Measure week-four adherence, not signups.',
    'Take the wellbeing-budget pitch to three hospital HR leads. You need a price and a procurement path, not interest.',
    'Test whether schedule import is the real unlock: half the cohort with roster sync, half typing shifts manually.',
  ],
} as const;

export const TRUST = {
  title: 'Your idea stays yours.',
  body: "Your idea is processed by models we run under a zero-retention agreement. It's never used as training data, never published, never shared. No human reads it unless you ask us to. Delete everything, any time, with one link.",
  link: 'Read the privacy note →',
} as const;

export const FAQ = [
  {
    q: 'Is it really free?',
    a: "Yes. The research runs on models, not a billable team, so one more report costs us close to nothing — and we're calibrating the engine, so real ideas sharpen it. Nothing interrupts the flow to sell you something; optional extras exist only after your report lands. Capped at 200 reports a day.",
  },
  {
    q: 'How long does it take?',
    a: "The interview takes about six minutes. The report arrives within 48 hours — most land in under 30. If we're running late, we email you rather than leave you guessing.",
  },
  {
    q: 'Who sees my idea?',
    a: "No one. It's processed under a zero-retention agreement, never used as training data, and never shared or published. Delete it any time and it's gone within 24 hours.",
  },
  {
    q: "What's in the report?",
    a: "Two PDFs: a 10-slide executive deck with the verdict, scores and competitive map, and a detailed report with market sizing, competitor teardowns, timing evidence, a risk register, and the three experiments we'd run next — all with sources.",
  },
] as const;
